import { Analytics } from '@mui/icons-material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import ClassOutlinedIcon from '@mui/icons-material/ClassOutlined';
import ContactsIcon from '@mui/icons-material/ContactsOutlined';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CreditCardOffIcon from '@mui/icons-material/CreditCardOff';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DisplaySettingsOutlinedIcon from '@mui/icons-material/DisplaySettingsOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import HealthAndSafetyOutlinedIcon from '@mui/icons-material/HealthAndSafetyOutlined';
import HistoryIcon from '@mui/icons-material/History';
import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined';
import InsertLinkOutlinedIcon from '@mui/icons-material/InsertLinkOutlined';
import Inventory2Icon from '@mui/icons-material/Inventory2Outlined';
import ListAltRoundedIcon from '@mui/icons-material/ListAltRounded';
import LocalAtmOutlinedIcon from '@mui/icons-material/LocalAtmOutlined';
import NotificationsIcon from '@mui/icons-material/NotificationsActiveOutlined';
import Paid from '@mui/icons-material/Paid';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import PriceChangeOutlinedIcon from '@mui/icons-material/PriceChangeOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import SdCardAlertOutlinedIcon from '@mui/icons-material/SdCardAlertOutlined';
import SettingsIcon from '@mui/icons-material/Settings';
import SummarizeOutlinedIcon from '@mui/icons-material/SummarizeOutlined';
import TaskOutlinedIcon from '@mui/icons-material/TaskOutlined';
import ThreeSixtyOutlined from '@mui/icons-material/ThreeSixtyOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import paths from '../../router/paths';
import { isOrgAppVersionV22 } from '../../utils/org-app-version';

const NDIS_V22_MENU_ORDER = [
  'analytics',
  'participants',
  'participants-360-view',
  'service-bookings',
  'budget',
  'roster-of-care',
  'service-delivery',
  'ndis-claims',
  'all-transactions',
  'ar-module',
  'settings',
];

const NDIS_V22_AGENCY_CLAIM_KEYS = [
  'ready-to-claim',
  'recovery-claim',
  'claim-batch-history',
  'successful-claims',
  'payment-cancellation',
  'remittance',
];

const NDIS_V22_PLSL_KEYS = ['ready-to-invoice', 'issued-invoices'];

const createIcon = (IconComponent) => <IconComponent className="menuIcon" fontSize="small" />;

const createMenuItem = (config) => config;

const createSubMenuLabel = (key, title) => ({
  key,
  title,
  isLabel: true,
});

const createItemMap = (items = []) =>
  new Map(items.filter(Boolean).map((item) => [item.key, item]));

const pickItemsByKey = (itemMap, keys = []) => keys.map((key) => itemMap.get(key)).filter(Boolean);

const getLabelValue = (labels, key, defaultValue) => labels[key] || defaultValue;

const hasSubMenu = (item) => Array.isArray(item?.subMenu) && item.subMenu.length > 0;

const getMenuItemByKey = (items, key) => items.find((item) => item.key === key);

const renameMenuItem = (item, title) => (item ? { ...item, title } : item);

const createLabeledSubMenuGroup = (labelKey, labelTitle, items) =>
  items.length ? [createSubMenuLabel(labelKey, labelTitle), ...items] : [];

const createClaimManagementSubMenu = () => [
  createMenuItem({
    key: 'ready-to-claim',
    title: 'Ready to Claim',
    icon: createIcon(CreditCardIcon),
    to: paths.readyToClaim,
    permission: 'ready-to-claim-page:visit',
  }),
  createMenuItem({
    key: 'recovery-claim',
    title: 'Claim Recovery',
    icon: createIcon(PriceChangeOutlinedIcon),
    to: paths.recoveryClaim,
    permission: 'claim-recovery-page:visit',
  }),
  createMenuItem({
    key: 'successful-claims',
    title: 'Successful Claims',
    icon: createIcon(EventAvailableOutlinedIcon),
    to: paths.successfulClaims,
    permission: 'successful-claims-page:visit',
  }),
  createMenuItem({
    key: 'payment-cancellation',
    title: 'Cancelled Claims',
    icon: createIcon(EventAvailableOutlinedIcon),
    to: paths.paymentCancellation,
    permission: 'payment-cancellation-page:visit',
  }),
  createMenuItem({
    key: 'claim-batch-history',
    title: 'Claim Batch History',
    icon: createIcon(AccountBalanceIcon),
    to: paths.claimBatchHistory,
    permission: 'claim-batch-history-page:visit',
  }),
  createMenuItem({
    key: 'claims-in-error',
    title: 'Claims in Error',
    icon: createIcon(CreditCardOffIcon),
    to: paths.claimsInError,
    permission: 'claims-in-error-page:visit',
  }),
  createMenuItem({
    key: 'remittance',
    title: 'Remittance',
    icon: createIcon(HandshakeOutlinedIcon),
    to: paths.remittance,
    permission: 'remittance-page:visit',
  }),
];

const createInvoiceManagementSubMenu = () => [
  createMenuItem({
    key: 'ready-to-invoice',
    title: 'Ready to Invoice',
    icon: createIcon(UploadFileOutlinedIcon),
    to: paths.readyToInvoice,
    permission: 'ready-to-invoice-page:visit',
  }),
  createMenuItem({
    key: 'issued-invoices',
    title: 'Issued Invoices',
    icon: createIcon(TaskOutlinedIcon),
    to: paths.issuedInvoices,
    permission: 'issued-invoices-page:visit',
  }),
  createMenuItem({
    key: 'invoices-batch-history',
    title: 'Invoice Batch History',
    icon: createIcon(Inventory2Icon),
    to: paths.invoiceBatchHistory,
    permission: 'invoice-batch-history-page:visit',
  }),
  createMenuItem({
    key: 'invoices-in-error',
    title: 'Invoices in Error',
    icon: createIcon(SdCardAlertOutlinedIcon),
    to: paths.invoicesInError,
    permission: 'invoices-in-error-page:visit',
  }),
];

const createNdisSettingsSubMenu = (labels = {}) => [
  createMenuItem({
    key: 'legal-entity-management',
    title: 'Legal Entity',
    icon: createIcon(HandshakeOutlinedIcon),
    to: paths.legalEntityManagement,
    permission: 'legal-entity-management-page:visit',
  }),
  createMenuItem({
    key: 'registrations',
    title: 'NDIS Registrations',
    icon: createIcon(HowToRegOutlinedIcon),
    to: paths.registrations,
    permission: 'registrations-page:visit',
  }),
  createMenuItem({
    key: 'product',
    title: 'Product',
    icon: createIcon(Inventory2Icon),
    to: paths.products,
    permission: 'products-page:visit',
  }),
  createMenuItem({
    key: 'category-x',
    title: getLabelValue(labels, 'categoryX', 'Category X'),
    icon: createIcon(ClassOutlinedIcon),
    to: paths.categoryX,
    permission: 'category-x-page:visit',
  }),
  createMenuItem({
    key: 'category-y',
    title: getLabelValue(labels, 'categoryY', 'Category Y'),
    icon: createIcon(ClassOutlinedIcon),
    to: paths.categoryY,
    permission: 'category-y-page:visit',
  }),
  createMenuItem({
    key: 'financial-contact',
    title: 'Financial Contacts',
    icon: createIcon(ContactsIcon),
    to: paths.financialContact,
    permission: 'financial-contact-page:visit',
  }),
  createMenuItem({
    key: 'ndis-notifications',
    title: 'NDIS Notification',
    icon: createIcon(NotificationsIcon),
    to: paths.ndisNotifications,
    permission: 'ndis-notifications-page:visit',
  }),
  createMenuItem({
    key: 'integration',
    title: 'Integration',
    icon: createIcon(InsertLinkOutlinedIcon),
    to: paths.integration,
    permission: 'integration-page:visit',
    divider: false,
  }),
  createMenuItem({
    key: 'labels',
    title: 'Label Management',
    icon: createIcon(DisplaySettingsOutlinedIcon),
    to: paths.labels,
    permission: 'label-management-page:visit',
    divider: false,
  }),
];

const createSahSettingsSubMenu = (labels = {}) => [
  createMenuItem({
    key: 'legal-entity-management',
    title: 'Legal Entity',
    icon: createIcon(HandshakeOutlinedIcon),
    to: paths.legalEntityManagement,
    permission: 'legal-entity-management-page:visit',
  }),
  createMenuItem({
    key: 'financial-contact',
    title: 'Financial Contacts',
    icon: createIcon(ContactsIcon),
    to: paths.financialContact,
    permission: 'financial-contact-page:visit',
  }),
  createMenuItem({
    key: 'category-x',
    title: getLabelValue(labels, 'categoryX', 'Category X'),
    icon: createIcon(ClassOutlinedIcon),
    to: paths.categoryX,
    permission: 'category-x-page:visit',
  }),
  createMenuItem({
    key: 'category-y',
    title: getLabelValue(labels, 'categoryY', 'Category Y'),
    icon: createIcon(ClassOutlinedIcon),
    to: paths.categoryY,
    permission: 'category-y-page:visit',
  }),
  createMenuItem({
    key: 'integration',
    title: 'Integration',
    icon: createIcon(InsertLinkOutlinedIcon),
    to: paths.integration,
    permission: 'integration-page:visit',
    divider: false,
  }),
  createMenuItem({
    key: 'labels',
    title: 'Label Management',
    icon: createIcon(DisplaySettingsOutlinedIcon),
    to: paths.labels,
    permission: 'label-management-page:visit',
    divider: false,
  }),
];

const createArModuleSubMenu = () => [
  createMenuItem({
    key: 'person-management',
    title: 'Person Management',
    icon: createIcon(PeopleAltOutlinedIcon),
    to: paths.personManagement,
    permission: 'ar-person-management-page:visit',
  }),
  createMenuItem({
    key: 'all-invoices',
    title: 'Invoices',
    icon: createIcon(ReceiptLongOutlinedIcon),
    to: paths.invoices,
    permission: 'ar-invoices-page:visit',
  }),
  createMenuItem({
    key: 'invoice-batches',
    title: 'Invoice Batches',
    icon: createIcon(Inventory2Icon),
    to: paths.invoiceBatches,
    permission: 'ar-invoice-batches-page:visit',
  }),
  createMenuItem({
    key: 'payments',
    title: 'Payments',
    icon: createIcon(AccountBalanceWalletOutlinedIcon),
    to: paths.paymentsV2,
    permission: 'ar-payments-page:visit',
  }),
  createMenuItem({
    key: 'ar-settlement',
    title: 'Settlement',
    icon: createIcon(DescriptionOutlinedIcon),
    to: paths.arSettlement,
    permission: 'ar-settlement-page:visit',
  }),
  createMenuItem({
    key: 'payer-statements',
    title: 'Payer Statements',
    icon: createIcon(SummarizeOutlinedIcon),
    to: paths.payerStatements,
    permission: 'ar-payer-statements-page:visit',
  }),
  createMenuItem({
    key: 'financial-contact',
    title: 'Financial Contacts',
    icon: createIcon(ContactsIcon),
    to: paths.financialContact,
    permission: 'financial-contact-page:visit',
  }),
];

const createSahStatementSubMenu = () => [
  createMenuItem({
    key: 'sah-monthly-statements',
    title: 'Monthly (Bulk)',
    icon: createIcon(DescriptionOutlinedIcon),
    to: paths.supportAtHomeMonthlyStatements,
    permission: 'support-at-home-statement-page:visit',
  }),
  createMenuItem({
    key: 'sah-care-recipient-statements',
    title: 'Care Recipient',
    icon: createIcon(PeopleAltOutlinedIcon),
    to: paths.supportAtHomeCareRecipientStatements,
    permission: 'support-at-home-statement-page:visit',
  }),
  createMenuItem({
    key: 'sah-statement-settings',
    title: 'Settings',
    icon: createIcon(DescriptionOutlinedIcon),
    to: paths.supportAtHomeStatementSettings,
    permission: 'support-at-home-statement-page:visit',
  }),
];

const createSupportAtHomeSubMenu = () => [
  createMenuItem({
    key: 'care-recipients',
    title: 'Care Recipients',
    icon: createIcon(PeopleAltOutlinedIcon),
    to: paths.supportAtHomeCareRecipients,
    permission: 'support-at-home-care-recipients-page:visit',
  }),
  createMenuItem({
    key: 'care-recipient-360',
    title: 'Care Recipient 360',
    icon: createIcon(ThreeSixtyOutlined),
    to: paths.supportAtHomeCareRecipient360,
    permission: 'support-at-home-care-recipient-360-page:visit',
  }),
  createMenuItem({
    key: 'sah-budget',
    title: 'Budget',
    icon: createIcon(Paid),
    to: paths.supportAtHomeBudget,
    permission: 'support-at-home-budget-page:visit',
  }),
  createMenuItem({
    key: 'transactions',
    title: 'Transactions',
    icon: createIcon(ReceiptLongOutlinedIcon),
    to: paths.supportAtHomeTransactions,
    permission: 'support-at-home-transactions-page:visit',
  }),
  createMenuItem({
    key: 'sah-claims',
    title: 'Claims',
    icon: createIcon(PaymentsOutlinedIcon),
    to: paths.supportAtHomeClaims,
    permission: 'support-at-home-claims-page:visit',
  }),
  createMenuItem({
    key: 'sah-ready-to-invoice',
    title: 'Ready to Invoice',
    icon: createIcon(UploadFileOutlinedIcon),
    to: paths.supportAtHomeReadyToInvoice,
    permission: 'support-at-home-ready-to-invoice-page:visit',
  }),
  createMenuItem({
    key: 'sah-statements',
    title: 'Statement',
    icon: createIcon(DescriptionOutlinedIcon),
    to: paths.lock,
    permission: 'support-at-home-statement-page:visit',
    subMenu: createSahStatementSubMenu(),
  }),
  createMenuItem({
    key: 'sah-payment',
    title: 'Payment',
    icon: createIcon(AccountBalanceWalletOutlinedIcon),
    to: paths.lock,
    permission: 'support-at-home-payment-page:visit',
  }),
  createMenuItem({
    key: 'sah-care-management',
    title: 'Care Management',
    icon: createIcon(HealthAndSafetyOutlinedIcon),
    to: paths.lock,
    permission: 'support-at-home-care-management-page:visit',
  }),
  createMenuItem({
    key: 'sah-providers',
    title: 'Providers',
    icon: createIcon(HowToRegOutlinedIcon),
    to: paths.supportAtHomeProviders,
    permission: 'support-at-home-providers-page:visit',
  }),
  createMenuItem({
    key: 'sah-pricing',
    title: 'Pricing',
    icon: createIcon(LocalAtmOutlinedIcon),
    to: paths.supportAtHomePricing,
    permission: 'support-at-home-pricing-page:visit',
  }),
  createMenuItem({
    key: 'ar-module',
    title: 'AR Module',
    icon: createIcon(DescriptionOutlinedIcon),
    to: paths.lock,
    permission: 'account-receivable-page:visit',
    subMenu: createArModuleSubMenu(),
  }),
];

const createSettingsMenu = (subMenu) =>
  createMenuItem({
    key: 'settings',
    title: 'Settings',
    icon: createIcon(SettingsIcon),
    to: paths.lock,
    permission: 'setting-page:visit',
    subMenu,
  });

const createBaseMenuItems = (labels = {}) => [
  createMenuItem({
    key: 'analytics',
    title: 'Analytics',
    icon: createIcon(Analytics),
    to: paths.analytics,
    permission: 'analytics-page:visit',
  }),
  createMenuItem({
    key: 'participants',
    title: 'Participants',
    icon: createIcon(PeopleAltOutlinedIcon),
    to: paths.participants,
    permission: 'participants-page:visit',
  }),
  createMenuItem({
    key: 'participants-360-view',
    title: 'Participant 360° View',
    icon: createIcon(ThreeSixtyOutlined),
    to: paths.participant360View,
    permission: 'participants-360-view-page:visit',
  }),
  createMenuItem({
    key: 'service-bookings',
    title: 'Service Bookings',
    icon: createIcon(EventAvailableOutlinedIcon),
    to: paths.serviceBookings,
    permission: 'service-bookings-page:visit',
  }),
  createMenuItem({
    key: 'budget',
    title: 'Budget',
    icon: createIcon(Paid),
    to: paths.budget,
    permission: 'budget-page:visit',
  }),
  createMenuItem({
    key: 'roster-of-care',
    title: 'Roster of Care',
    icon: createIcon(ListAltRoundedIcon),
    to: paths.rosterOfCare,
    permission: 'roster-of-care-page:visit',
  }),
  createMenuItem({
    key: 'service-delivery',
    title: 'Service Delivery',
    icon: createIcon(ListAltRoundedIcon),
    to: `${paths.serviceDelivery}?state=processing`,
    permission: 'service-delivery-page:visit',
  }),
  createMenuItem({
    key: 'claim-management',
    title: 'Claim Management',
    icon: createIcon(PaymentsOutlinedIcon),
    to: paths.lock,
    permission: 'claim-management-page:visit',
    subMenu: createClaimManagementSubMenu(),
  }),
  createMenuItem({
    key: 'invoices',
    title: 'Invoice Management',
    icon: createIcon(DescriptionOutlinedIcon),
    to: paths.lock,
    permission: 'invoices-page:visit',
    subMenu: createInvoiceManagementSubMenu(),
  }),
  createSettingsMenu(createNdisSettingsSubMenu(labels)),
  createMenuItem({
    key: 'support-at-home',
    title: 'Support At Home',
    icon: createIcon(PeopleAltOutlinedIcon),
    to: paths.lock,
    permission: 'support-at-home-page:visit',
    subMenu: createSupportAtHomeSubMenu(),
  }),
];

const createAllTransactionsMenuItem = () =>
  createMenuItem({
    key: 'all-transactions',
    title: 'All Transactions',
    icon: createIcon(HistoryIcon),
    to: paths.transactions,
    permission: 'transactions-page:visit',
  });

const createV22NdisClaimsItem = (legacyItemMap) => {
  const claimManagementItem = legacyItemMap.get('claim-management');
  const invoiceManagementItem = legacyItemMap.get('invoices');

  if (!claimManagementItem) {
    return null;
  }

  const claimSubMenuMap = createItemMap(claimManagementItem.subMenu || []);
  const invoiceSubMenuMap = createItemMap(invoiceManagementItem?.subMenu || []);
  const agencyClaimItems = pickItemsByKey(claimSubMenuMap, NDIS_V22_AGENCY_CLAIM_KEYS).map(
    (item) => (item.key === 'successful-claims' ? renameMenuItem(item, 'Successful Claims') : item),
  );
  const plslItems = pickItemsByKey(invoiceSubMenuMap, NDIS_V22_PLSL_KEYS).map((item) => {
    if (item.key === 'issued-invoices') {
      return { ...item, to: paths.issuedInvoicesV2 };
    }
    return item;
  });

  return {
    ...claimManagementItem,
    key: 'ndis-claims',
    title: 'NDIS Claims',
    subMenu: [
      ...createLabeledSubMenuGroup('agency-claims-label', 'Agency Claims', agencyClaimItems),
      ...createLabeledSubMenuGroup('plsl-label', 'PLSL', plslItems),
    ],
  };
};

const flattenMenuItems = (items = []) =>
  items.map((item) =>
    hasSubMenu(item)
      ? item
      : {
          key: item.key,
          title: item.title,
          icon: item.icon,
          to: item.to,
          permission: item.permission,
        },
  );

export const getMenuItems = (labels = {}) => createBaseMenuItems(labels);

const getLegacyNdisMenuItems = (labels = {}) =>
  createBaseMenuItems(labels).filter((item) => item.key !== 'support-at-home');

const getV22NdisMenuItems = (labels = {}) => {
  const legacyNdisItems = getLegacyNdisMenuItems(labels);
  const baseMenuItems = createBaseMenuItems(labels);
  const supportAtHomeItem = getMenuItemByKey(baseMenuItems, 'support-at-home');
  const arModuleItem = getMenuItemByKey(supportAtHomeItem?.subMenu || [], 'ar-module');
  const ndisItemMap = createItemMap([
    ...legacyNdisItems,
    arModuleItem,
    createAllTransactionsMenuItem(),
    createV22NdisClaimsItem(createItemMap(legacyNdisItems)),
  ]);

  return pickItemsByKey(ndisItemMap, NDIS_V22_MENU_ORDER);
};

export const getNdisMenuItems = (labels = {}, appVersion = '') =>
  isOrgAppVersionV22(appVersion) ? getV22NdisMenuItems(labels) : getLegacyNdisMenuItems(labels);

export const getSahMenuItems = (labels = {}) => {
  const baseMenuItems = createBaseMenuItems(labels);
  const analyticsItem = getMenuItemByKey(baseMenuItems, 'analytics');
  const supportAtHomeItem = getMenuItemByKey(baseMenuItems, 'support-at-home');
  const sahSettingsItem = createSettingsMenu(createSahSettingsSubMenu(labels));

  return [
    analyticsItem,
    ...flattenMenuItems(supportAtHomeItem?.subMenu || []),
    sahSettingsItem,
  ].filter(Boolean);
};
